import { queryOptions } from '@tanstack/react-query';
import { mediaHttp } from '@/lib/api/http-client';
import { parseResponse } from '@/lib/api/parse-response';
import type { ProjectPerformance } from '../model/performance.types';
import { MEDIA_CAMPAIGN_QUERY_KEYS } from './query-keys';
import { projectPerformanceSchema } from './schemas';

export interface PerformanceParams {
  startDate: string;
  endDate: string;
  dimension?: 'platform' | 'campaign' | 'creative' | 'placement';
  campaignId?: string;
  platforms?: string[];
  channels?: string[];
}

/**
 * Sorted before joining so `['dv360','meta_ads']` and `['meta_ads','dv360']` produce the
 * same string - one selection, whichever order a `Set` or the toolbar happened to build it in.
 */
function commaJoined(values?: string[]): string | undefined {
  return values?.length ? [...values].sort().join(',') : undefined;
}

/**
 * A project's delivery over a window, already deduplicated by campaign role and grouped
 * by currency on the server — so the client never has to decide which platform's
 * impressions to trust.
 */
export const projectPerformanceQueryOptions = (projectId: string, params: PerformanceParams) => {
  const dimension = params.dimension ?? 'platform';
  const platforms = commaJoined(params.platforms);
  const channels = commaJoined(params.channels);

  return queryOptions({
    queryKey: MEDIA_CAMPAIGN_QUERY_KEYS.performance(
      projectId,
      params.startDate,
      params.endDate,
      dimension,
      params.campaignId,
      platforms,
      channels,
    ),
    queryFn: async ({ signal }) => {
      const res = await mediaHttp.get({
        responseType: 'json',
        security: [{ scheme: 'bearer', type: 'http' }],
        url: `/api/v1/media-data/projects/${projectId}/performance`,
        query: {
          startDate: params.startDate,
          endDate: params.endDate,
          dimension,
          ...(params.campaignId ? { campaignId: params.campaignId } : {}),
          ...(platforms ? { platforms } : {}),
          ...(channels ? { channels } : {}),
        },
        signal,
      });
      return parseResponse(projectPerformanceSchema, res);
    },
  });
};

/**
 * The one spend figure a headline tile may print, or null when there is none.
 *
 * Null covers two different truths — mixed currencies, and nothing reporting spend — and
 * both must render as an em dash rather than a number. Summing across currencies here
 * would be the exact error the spec forbids.
 */
export function totalSpendOf(
  performance: Pick<ProjectPerformance, 'isSingleCurrency' | 'spendByCurrency'>,
): { amount: number; currency: string } | null {
  if (!performance.isSingleCurrency || performance.spendByCurrency.length !== 1) return null;
  const [only] = performance.spendByCurrency;
  return { amount: only.spend, currency: only.currency };
}
